import { H264Decoder, type DecoderStats, type FrameSink } from './h264_decoder.ts';
import { H264Depacketizer, type AccessUnit, type DepacketizerStats } from './h264_depacketizer.ts';
import { parseRtpPacket } from '../rtsp/rtp.ts';

/** What the pipeline needs to know about the track of a session. Plain data: it crosses to a Worker. */
export interface PipelineTrack {
    payloadType: number;
    clockRate: number;
    sps?: Uint8Array | undefined;
    pps?: Uint8Array | undefined;
}

export interface PipelineStats {
    depacketizer: DepacketizerStats;
    decoder: DecoderStats;
}

/**
 * RTP packets (or whole access units) in, decoded pictures out: depacketiser and decoder of one stream. It runs in the
 * stream's Worker, or on the main thread where a Worker cannot decode. A new session (after a
 * reconnect) starts with a fresh depacketiser and decoder.
 */
export class H264Pipeline {
    readonly #sink: FrameSink;
    readonly #log: (message: string) => void;
    #session: { payloadType: number; depacketizer: H264Depacketizer; decoder: H264Decoder } | null = null;
    #last: PipelineStats | null = null;

    constructor(sink: FrameSink, log: (message: string) => void = () => {}) {
        this.#sink = sink;
        this.#log = log;
    }

    start(track: PipelineTrack) : void {
        this.end();
        this.#last = null;
        const decoder = new H264Decoder(this.#sink, { clockRate: track.clockRate, log: this.#log });
        const depacketizer = new H264Depacketizer({
            sps: track.sps,
            pps: track.pps,
            onAccessUnit: (unit) => decoder.push(unit),
        });
        this.#session = { payloadType: track.payloadType, depacketizer, decoder };
    }

    /** RTP packets as received, and when they arrived (`wallNow()`). */
    push(packets: readonly Uint8Array[], receivedAt: number) : void {
        if (!this.#session) {
            return;
        }

        for (const bytes of packets) {
            const packet = parseRtpPacket(bytes);
            if (packet && packet.payloadType === this.#session.payloadType) {
                this.#session.depacketizer.push(packet, receivedAt);
            }
        }
    }

    /** Access units already whole (an HLS stream's samples, video-hls.md VH-3): to the decoder as they are. */
    pushUnits(units: readonly AccessUnit[]) : void {
        for (const unit of units) {
            this.#session?.decoder.push(unit);
        }
    }

    /** Ends the session; the numbers of the ended session stay readable. */
    end() : void {
        if (!this.#session) {
            return;
        }

        this.#last = this.stats();
        this.#session.decoder.close();
        this.#session = null;
    }

    /** Null before the first session. */
    stats() : PipelineStats | null {
        if (!this.#session) {
            return this.#last;
        }

        return { depacketizer: { ...this.#session.depacketizer.stats }, decoder: this.#session.decoder.stats() };
    }
}
